import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Info, Search, X, AlertTriangle } from "lucide-react";
import { useHotkeys } from "react-hotkeys-hook";
import clsx from "clsx";

import { Popover } from "@/design-system/components/Popover/Popover";
import { Input } from "@/design-system/components/Input/Input";
import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import { Accordion } from "@/design-system/components/Accordion/Accordion";
import { AutoDetectedTag } from "@/InsightsPage/components/AutoDetectedTag";
import { FunnelEvents } from "@/api/insights";

import { EventListItem } from "./EventListItem";
import { EventFilterMenu } from "./EventFilterMenu";
import { EventDetailsPanel } from "./EventDetailsPanel";
import { SORT_BY_OPTIONS, SortByOption } from "../constants";

function otherEventsStartOpen(
  eventDefs: FunnelEvents[],
  expandWhenNoneRecommended: boolean,
): string | undefined {
  if (!expandWhenNoneRecommended) {
    return undefined;
  }
  const visible = eventDefs.filter((event) => !event.isHidden);
  const hasRecommended = visible.some((event) => event.isRecommended);
  const hasOther = visible.some(
    (event) => event.isOther || !event.isRecommended,
  );
  return !hasRecommended && hasOther ? "otherEvents" : undefined;
}

interface EventSearchMultiSelectProps {
  eventDefs: FunnelEvents[];
  selectedEventDefIds: string[];
  onSelectionChange: (eventDefIds: string[]) => void;
  placeholder?: string;
  maxSelected?: number;
  disabled?: boolean;
  error?: boolean;
  /**
   * Override the popover's side relative to the trigger. By default the
   * popover renders to the right on wide viewports and below on narrower
   * ones — sized for the funnel-builder modal where the trigger sits in a
   * narrow left column. Pass an explicit value (e.g. "bottom") when the
   * trigger spans a wider container and there isn't room to render to the
   * side without overflowing.
   */
  popoverSide?: "right" | "bottom";
  /**
   * Open Other events on first render when none of the events are
   * recommended. Manage Conversions leaves this off.
   */
  expandOtherEventsWhenNoneRecommended?: boolean;
  "data-track"?: string;
}

export function EventSearchMultiSelect({
  eventDefs,
  selectedEventDefIds,
  onSelectionChange,
  placeholder = "Select events",
  maxSelected,
  disabled,
  error,
  popoverSide: popoverSideProp,
  expandOtherEventsWhenNoneRecommended = false,
  "data-track": dataTrack,
}: EventSearchMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);
  const [popoverSide, setPopoverSide] = useState<"right" | "bottom">("right");
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortByOption>(SORT_BY_OPTIONS.NAME);
  const [filterByTag, setFilterByTag] = useState<string | null>(null);
  const [filterByIntegration, setFilterByIntegration] = useState<string | null>(
    null,
  );
  const [accordionValue, setAccordionValue] = useState<string | undefined>(() =>
    otherEventsStartOpen(eventDefs, expandOtherEventsWhenNoneRecommended),
  );

  useEffect(() => {
    if (popoverSideProp) {
      setPopoverSide(popoverSideProp);
      return;
    }
    const updatePopoverSide = () => {
      setPopoverSide(window.innerWidth < 1450 ? "bottom" : "right");
    };
    updatePopoverSide();
    window.addEventListener("resize", updatePopoverSide);
    return () => window.removeEventListener("resize", updatePopoverSide);
  }, [popoverSideProp]);

  const resetFilters = useCallback(() => {
    setSortBy(SORT_BY_OPTIONS.NAME);
    setFilterByTag(null);
    setFilterByIntegration(null);
  }, []);

  const hasActiveFilters =
    sortBy !== SORT_BY_OPTIONS.NAME ||
    filterByTag !== null ||
    filterByIntegration !== null;

  const allEventDefs = eventDefs;

  const uniqueTags = useMemo(() => {
    const tags = new Set<string>();
    allEventDefs.forEach((event) => {
      if (event.tags) {
        Object.keys(event.tags).forEach((tag) => tags.add(tag));
      }
    });
    return Array.from(tags).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
  }, [allEventDefs]);

  const uniqueIntegrations = useMemo(() => {
    const integrations = new Set<string>();
    allEventDefs.forEach((event) => {
      if (event.adPlatformsDeliveredList) {
        event.adPlatformsDeliveredList.split(",").forEach((platform) => {
          const trimmed = platform.trim();
          if (trimmed) integrations.add(trimmed);
        });
      }
    });
    return Array.from(integrations).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
  }, [allEventDefs]);

  const { recommendedEvents, otherEvents, allFilteredEvents } = useMemo(() => {
    const visibleEvents = allEventDefs.filter((f) => !f.isHidden);

    let filtered = query
      ? visibleEvents.filter((event) => {
          const safeQuery = query.toLowerCase();
          const nameMatch = event.name.toLowerCase().includes(safeQuery);
          const tagMatch = event.tags
            ? Object.keys(event.tags).some((tag) =>
                tag.toLowerCase().includes(safeQuery),
              )
            : false;
          return nameMatch || tagMatch;
        })
      : visibleEvents;

    if (filterByTag) {
      filtered = filtered.filter(
        (event) => event.tags && event.tags[filterByTag],
      );
    }

    if (filterByIntegration) {
      filtered = filtered.filter((event) =>
        event.adPlatformsDeliveredList
          ?.split(",")
          .map((p) => p.trim())
          .includes(filterByIntegration),
      );
    }

    const sortFn = (a: (typeof filtered)[0], b: (typeof filtered)[0]) => {
      if (sortBy === SORT_BY_OPTIONS.AD_PLATFORMS_DELIVERED) {
        return (b.adPlatformsDelivered ?? 0) - (a.adPlatformsDelivered ?? 0);
      }
      if (sortBy === SORT_BY_OPTIONS.EVENTS_DELIVERED) {
        return (b.totalEventsDelivered ?? 0) - (a.totalEventsDelivered ?? 0);
      }
      // sortBy default === "name"
      return a.name.localeCompare(b.name);
    };

    const recommended = filtered.filter((e) => e.isRecommended).sort(sortFn);
    const other = filtered
      .filter((e) => e.isOther || !e.isRecommended)
      .sort(sortFn);
    const sortedFiltered = [...recommended, ...other];

    return {
      recommendedEvents: recommended,
      otherEvents: other,
      allFilteredEvents: sortedFiltered,
    };
  }, [allEventDefs, query, filterByTag, filterByIntegration, sortBy]);

  const selectedEvents = useMemo(() => {
    if (!allEventDefs) return [];
    return allEventDefs.filter((event) =>
      selectedEventDefIds.includes(event.eventDefId),
    );
  }, [allEventDefs, selectedEventDefIds]);

  const focusedOption = useMemo(
    () => allFilteredEvents?.[focusedOptionIndex],
    [allFilteredEvents, focusedOptionIndex],
  );

  const resetFocusedOption = useCallback(() => {
    setFocusedOptionIndex(0);
  }, []);

  const focusNextOption = useCallback(() => {
    if (!allFilteredEvents) return;
    setFocusedOptionIndex((prevIndex) =>
      Math.min(prevIndex + 1, allFilteredEvents.length - 1),
    );
  }, [allFilteredEvents]);

  const focusPreviousOption = useCallback(() => {
    if (!allFilteredEvents) return;
    setFocusedOptionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  }, [allFilteredEvents]);

  const toggleEventSelection = useCallback(
    (eventDefId: string) => {
      if (selectedEventDefIds.includes(eventDefId)) {
        onSelectionChange(
          selectedEventDefIds.filter((id) => id !== eventDefId),
        );
      } else {
        if (maxSelected && selectedEventDefIds.length >= maxSelected) {
          return;
        }
        onSelectionChange([...selectedEventDefIds, eventDefId]);
      }
    },
    [selectedEventDefIds, onSelectionChange, maxSelected],
  );

  const toggleFocusedOption = useCallback(() => {
    if (focusedOption) {
      toggleEventSelection(focusedOption.eventDefId);
    }
  }, [focusedOption, toggleEventSelection]);

  const removeEvent = useCallback(
    (eventDefId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onSelectionChange(selectedEventDefIds.filter((id) => id !== eventDefId));
    },
    [selectedEventDefIds, onSelectionChange],
  );

  useEffect(resetFocusedOption, [allFilteredEvents, resetFocusedOption]);
  useHotkeys("up", focusPreviousOption, { enabled: open });
  useHotkeys("down", focusNextOption, { enabled: open });
  useHotkeys("enter", toggleFocusedOption, { enabled: open });
  useHotkeys("space", toggleFocusedOption, { enabled: open });

  const handleInputKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      focusPreviousOption();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      focusNextOption();
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleFocusedOption();
    }
  };

  const getGlobalIndex = useCallback(
    (eventDefId: string) =>
      allFilteredEvents.findIndex((e) => e.eventDefId === eventDefId),
    [allFilteredEvents],
  );

  const isEventDisabled = useCallback(
    (eventDefId: string) => {
      const isSelected = selectedEventDefIds.includes(eventDefId);
      return (
        !isSelected &&
        maxSelected !== undefined &&
        selectedEventDefIds.length >= maxSelected
      );
    },
    [selectedEventDefIds, maxSelected],
  );

  const handleSelectedEventClick = useCallback(
    (event: FunnelEvents, e: React.MouseEvent) => {
      e.stopPropagation();
      const index = getGlobalIndex(event.eventDefId);
      if (index !== -1) {
        setFocusedOptionIndex(index);
      }
      // Open accordion if event is in "other events"
      const isInOtherEvents = otherEvents.some(
        (e) => e.eventDefId === event.eventDefId,
      );
      if (isInOtherEvents) {
        setAccordionValue("otherEvents");
      }
      setOpen(true);

      // Scroll to the event after popover opens
      setTimeout(() => {
        const element = document.querySelector(
          `[data-event-id="${event.eventDefId}"]`,
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    },
    [getGlobalIndex, otherEvents],
  );

  return (
    <div className="group relative">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger disabled={disabled} asChild>
          <button
            type="button"
            data-track={dataTrack}
            className={clsx(
              "bg-surface-default flex min-h-12 w-full cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-left",
              error && "border-borderError",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <div className="flex flex-1 flex-wrap gap-1">
              {selectedEvents.length === 0 ? (
                <span className="text-textSubtle">{placeholder}</span>
              ) : (
                selectedEvents.map((event) => {
                  const isAlert = event.isOther;
                  return (
                    <span
                      key={event.eventDefId}
                      onClick={(e) => handleSelectedEventClick(event, e)}
                      onMouseEnter={(e) => handleSelectedEventClick(event, e)}
                      className={clsx(
                        "inline-flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 text-sm transition-colors hover:ring-1 hover:ring-blue-300",
                        isAlert
                          ? "bg-status-warning-tint text-status-warning hover:bg-status-warning/15"
                          : "bg-surface-muted hover:bg-surface-pressed",
                      )}
                    >
                      {isAlert && (
                        <AlertTriangle size={14} className="text-yellow-600" />
                      )}
                      {event.name}
                      <AutoDetectedTag
                        conversionSource={event.conversionSource}
                      />
                      <X
                        size={16}
                        className="text-textSubtle hover:text-textSecondary cursor-pointer"
                        onClick={(e) => removeEvent(event.eventDefId, e)}
                      />
                    </span>
                  );
                })
              )}
            </div>
            <ChevronDown size={20} className="text-iconMinimal flex-none" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn z-popover bg-surface-default rounded-md border shadow-lg"
            side={popoverSide}
            align="start"
            sideOffset={8}
            collisionPadding={16}
          >
            <div className="flex h-110 w-180 flex-col">
              <div className="border-borderSubtle flex-none border-b p-3">
                <Input
                  leadingIcon={<Search size={20} />}
                  placeholder="Search events by name or tag"
                  value={query}
                  onChange={(e) => setQuery(e.currentTarget.value)}
                  onKeyDown={handleInputKeydown}
                  className="h-10"
                  autoFocus
                />
              </div>
              <div className="divide-borderSubtle flex min-h-0 flex-1 divide-x">
                <div className="relative flex w-1/2 flex-col gap-4 overflow-auto overflow-x-hidden p-3">
                  <div>
                    <div className="flex justify-between">
                      <span className="text-textSubtle mb-2 flex gap-1 text-xs">
                        Recommended ({recommendedEvents.length})
                        <Tooltip
                          contentProps={{ className: "relative !z-[80]" }}
                          content="Based on if events recently delivered to supported integrations"
                        >
                          <Info size={16} />
                        </Tooltip>
                      </span>
                      <EventFilterMenu
                        open={filterMenuOpen}
                        onOpenChange={setFilterMenuOpen}
                        sortBy={sortBy}
                        onSortByChange={setSortBy}
                        filterByTag={filterByTag}
                        onFilterByTagChange={setFilterByTag}
                        uniqueTags={uniqueTags}
                        filterByIntegration={filterByIntegration}
                        onFilterByIntegrationChange={setFilterByIntegration}
                        uniqueIntegrations={uniqueIntegrations}
                        onReset={resetFilters}
                        hasActiveFilters={hasActiveFilters}
                      />
                    </div>
                    {recommendedEvents.map((event) => {
                      const globalIndex = getGlobalIndex(event.eventDefId);
                      return (
                        <EventListItem
                          key={event.eventDefId}
                          event={event}
                          isSelected={selectedEventDefIds.includes(
                            event.eventDefId,
                          )}
                          isDisabled={isEventDisabled(event.eventDefId)}
                          isFocused={globalIndex === focusedOptionIndex}
                          onMouseOver={() => setFocusedOptionIndex(globalIndex)}
                          onToggle={() =>
                            toggleEventSelection(event.eventDefId)
                          }
                        />
                      );
                    })}
                    {recommendedEvents.length === 0 && (
                      <div className="text-textSubtle py-4 text-center text-sm">
                        No recommended events found
                      </div>
                    )}
                  </div>

                  <Accordion
                    type="single"
                    key={otherEvents.length === 0 ? "empty" : "has-events"}
                    className="border-borderSubtle flex flex-col gap-1 border-t"
                    rootProps={{
                      value:
                        otherEvents.length === 0
                          ? "otherEvents"
                          : accordionValue,
                      onValueChange: setAccordionValue,
                    }}
                  >
                    <Accordion.Item
                      showMore={
                        <>
                          Other events ({otherEvents.length}){" "}
                          <Tooltip
                            contentProps={{ className: "relative !z-[80]" }}
                            content="Stale events or those delivering to unsupported integrations"
                          >
                            <Info size={16} />
                          </Tooltip>
                        </>
                      }
                      value="otherEvents"
                      triggerPosition="top"
                      triggerProps={{
                        className:
                          "text-textSubtle text-xs font-medium text-left",
                      }}
                      contentProps={{
                        className: "p-0",
                      }}
                    >
                      <div className="flex flex-col">
                        {otherEvents.map((event) => {
                          const globalIndex = getGlobalIndex(event.eventDefId);
                          return (
                            <EventListItem
                              key={event.eventDefId}
                              event={event}
                              isSelected={selectedEventDefIds.includes(
                                event.eventDefId,
                              )}
                              isDisabled={isEventDisabled(event.eventDefId)}
                              isFocused={globalIndex === focusedOptionIndex}
                              onMouseOver={() =>
                                setFocusedOptionIndex(globalIndex)
                              }
                              onToggle={() =>
                                toggleEventSelection(event.eventDefId)
                              }
                            />
                          );
                        })}
                      </div>
                      {otherEvents.length === 0 && (
                        <div className="text-textSubtle py-4 text-center text-sm">
                          No other events found
                        </div>
                      )}
                    </Accordion.Item>
                  </Accordion>
                </div>

                <EventDetailsPanel
                  focusedOption={focusedOption}
                  hasEvents={
                    recommendedEvents.length > 0 || otherEvents.length > 0
                  }
                />
              </div>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
